import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { MessageSquare, Camera, Activity, Wifi, Cpu, Layers, Palette, Search } from 'lucide-react';
import type { PetConfig } from '../pets/types';
import { toast } from 'sonner';

/**
 * 从 spritesheet dataURL 中裁切出第一帧（idle 行第 0 列）作为 40x40 缩略图
 */
function usePetThumbnail(pet: PetConfig | null): string | null {
  const [thumb, setThumb] = useState<string | null>(null);
  const lastSheetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pet?.spritesheet) return;
    if (pet.spritesheet === lastSheetRef.current) return;
    lastSheetRef.current = pet.spritesheet;

    const img = new Image();
    img.onload = () => {
      const cw = pet.atlas?.cellWidth || 192;
      const ch = pet.atlas?.cellHeight || 208;
      const size = 40;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, cw, ch, 0, 0, size, size);
      setThumb(canvas.toDataURL('image/png'));
      img.onload = null; img.onerror = null;
    };
    img.onerror = () => { lastSheetRef.current = null; };
    img.src = pet.spritesheet;

    return () => { img.onload = null; img.onerror = null; };
  }, [pet]);

  return thumb;
}

/**
 * 网络状态检测 hook — 监听 navigator.onLine + online/offline 事件
 */
function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}

interface DesktopDockProps {
  t: any;
  lang: string;
  onNewChat: () => void;
  onSkills: () => void;
  onTheme: () => void;
  onScreenshot: () => void;
  onSearch: () => void;
  callState: string;
  selectedPet: PetConfig | null;
  /** v3.12.4: 系统资源数据，用于状态指示器真实显示 */
  systemStats?: { cpuPercent?: number; memoryPercent?: number };
}

/** 快捷按钮 — v3.12.6: 颜色改用 celestial-* 设计令牌 */
const dockItems = [
  { icon: Search, label: '全局搜索', labelEn: 'Search', action: 'search' as const, color: 'text-celestial-glow', bg: 'hover:bg-celestial-glow/15' },
  { icon: MessageSquare, label: '对话', labelEn: 'Chat', action: 'newChat' as const, color: 'text-celestial-glow', bg: 'hover:bg-celestial-glow/15' },
  { icon: Layers, label: '技能中心', labelEn: 'Skills', action: 'skills' as const, color: 'text-celestial-nebula', bg: 'hover:bg-celestial-nebula/15' },
  { icon: Palette, label: '主题', labelEn: 'Theme', action: 'theme' as const, color: 'text-celestial-saturn', bg: 'hover:bg-celestial-saturn/15' },
];

// v3.12.6: React.memo 避免父组件渲染时无谓重渲（配合父组件 useCallback 稳定化回调）
export const DesktopDock = React.memo(function DesktopDock({ t, lang, onNewChat, onSkills, onTheme, onScreenshot, onSearch, callState, selectedPet, systemStats }: DesktopDockProps) {
  const isOnline = useOnlineStatus();

  const handleClick = (action: string) => {
    switch (action) {
      case 'newChat': onNewChat(); break;
      case 'skills': onSkills(); break;
      case 'theme': onTheme(); break;
      case 'screenshot': onScreenshot(); break;
      case 'search': onSearch(); break;
      default: console.warn(`[DesktopDock] Unknown action: ${action}`);
    }
  };

  const petThumb = usePetThumbnail(selectedPet);

  return (
    <div className="flex items-center gap-2" role="toolbar" aria-label={lang === 'zh' ? '桌面快捷操作栏' : 'Desktop quick actions'}>
      {/* 迷你宠物缩略图 */}
      {selectedPet && petThumb && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="shrink-0"
        >
          <motion.div
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.92 }}
            className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center overflow-hidden hover:bg-white/10 transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-celestial-saturn/60"
            onClick={() => toast.info(
              lang === 'zh'
                ? `${selectedPet.name} 🐱\n点击交互功能开发中`
                : `${selectedPet.name}\nInteraction coming soon`
            )}
            title={`${selectedPet.name}`}
            aria-label={lang === 'zh' ? `桌面宠物 ${selectedPet.name}` : `Desktop pet ${selectedPet.name}`}
          >
            <img
              src={petThumb}
              alt={lang === 'zh' ? `桌面宠物 ${selectedPet.name}` : `Desktop pet ${selectedPet.name}`}
              className="w-full h-full"
              style={{ imageRendering: 'pixelated' }}
              decoding="async"
            />
          </motion.div>
          <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5" role="status" aria-label={lang === 'zh' ? '宠物活跃状态' : 'Pet active status'}>
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_#4ade80]"
            />
          </div>
        </motion.div>
      )}

      <div className="w-px h-7 bg-white/[0.08]" aria-hidden="true" />

      {/* 快捷操作按钮 */}
      {dockItems.map((item) => (
        <button
          key={item.action}
          onClick={() => handleClick(item.action)}
          className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 ${item.bg} group focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-1 focus-visible:ring-offset-black`}
          aria-label={lang === 'zh' ? item.label : item.labelEn}
        >
          <item.icon size={16} className={`${item.color} group-hover:scale-110 transition-transform`} />
        </button>
      ))}

      <div className="w-px h-7 bg-white/[0.08]" aria-hidden="true" />

      {/* 截图按钮 */}
      <button
        onClick={() => handleClick('screenshot')}
        className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 hover:bg-blue-500/15 group focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-1 focus-visible:ring-offset-black"
        aria-label={lang === 'zh' ? '截图' : 'Screenshot'}
      >
        <Camera size={16} className="text-blue-400 group-hover:scale-110 transition-transform" />
      </button>

      <div className="w-px h-7 bg-white/[0.08]" aria-hidden="true" />

      {/* 系统状态指示器 */}
      <div className="flex items-center gap-0.5">
        {/* AI 状态 */}
        <motion.button
          whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
          onClick={() => {
            const aiStatus = callState !== 'idle'
              ? (callState === 'speaking' ? (lang === 'zh' ? '🎙️ AI 正在说话' : '🎙️ AI Speaking')
                : callState === 'listening' ? (lang === 'zh' ? '🎙️ AI 正在聆听...' : '🎙️ AI Listening...')
                : (lang === 'zh' ? '🎙️ AI 处理中...' : '🎙️ AI Processing...'))
              : (lang === 'zh' ? '✅ AI 待命就绪' : '✅ AI Ready');
            toast.info(aiStatus);
          }}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/[0.06] transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-white/40"
          aria-label={lang === 'zh' ? 'AI 状态' : 'AI Status'}
        >
          <motion.div
            animate={callState !== 'idle' ? { scale: [1, 1.2, 1] } : {}}
            transition={callState !== 'idle' ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : {}}
            role="status"
          >
            <Activity size={14} className={callState !== 'idle' ? 'text-cyan-400' : 'text-green-400'} />
          </motion.div>
        </motion.button>

        {/* 网络状态 — v3.12.4: 接入真实 navigator.onLine */}
        <motion.button
          whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
          onClick={() => toast.info(isOnline
            ? (lang === 'zh' ? '📡 网络连接正常' : '📡 Network OK')
            : (lang === 'zh' ? '⚠️ 网络已断开' : '⚠️ Network offline'))}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/[0.06] transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-white/40"
          aria-label={lang === 'zh' ? '网络状态' : 'Network Status'}
        >
          <Wifi size={14} className={isOnline ? 'text-white/45' : 'text-red-400'} />
        </motion.button>

        {/* 系统 — v3.12.4: 接入真实 systemStats */}
        <motion.button
          whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
          onClick={() => {
            if (systemStats?.cpuPercent !== undefined) {
              const cpu = Math.round(systemStats.cpuPercent);
              const mem = systemStats.memoryPercent !== undefined ? Math.round(systemStats.memoryPercent) : '?';
              toast.info(lang === 'zh'
                ? `💻 CPU ${cpu}% · 内存 ${mem}%`
                : `💻 CPU ${cpu}% · Memory ${mem}%`);
            } else {
              toast.info(lang === 'zh' ? '💻 系统运行正常' : '💻 System OK');
            }
          }}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/[0.06] transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-white/40"
          aria-label={lang === 'zh' ? '系统状态' : 'System Status'}
        >
          <Cpu size={14} className={systemStats ? 'text-white/65' : 'text-white/45'} />
        </motion.button>
      </div>
    </div>
  );
});
